import React, { useEffect, useMemo, useState } from 'react';
import { InputControl, SelectControl, Row, Col } from '@si-armes/dsfr-react';
import * as PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { getAPI, reinitialiseAPI } from 'store/apiReducer';
import { useFormContext } from 'react-hook-form';
import {
    formaterOptions,
    getMessageErreurChampManquant,
    verifierSiDepartementEstOuvertALaCreationDeCompte,
} from '../../../utils/utils';
import { REGEX_RGA, REGEXP_CODE_POSTAL } from '../../../constants/contraintes.constants';
import usagerClientReducer from '../../../store/usagerReducer';
import {
    API_RECUPERER_ADRESSE_LISTE_EXTENSION,
    API_VERIFIER_CODE_POSTAL_EXISTANT,
    CONTEXT_API_RECUPERER_ADRESSE_LISTE_EXTENSION,
    CONTEXT_API_VERIFIER_CODE_POSTAL,
} from '../../../constants/apiUrls.constants';

const FormulaireAdresseSimple = ({
    desactiverChamps = false,
    autofilledFields = {},
    prefix,
    getApi,
    reinitialiseApi,
    isLoadingListeExtension,
    dataListeExtension,
    errorListeExtension,
    isLoadingCodePostal,
    dataCodePostal,
    errorCodePostal,
    messageCodePostal,
}) => {
    const dispatch = useDispatch();
    const { register, formState, watch, trigger, setError, clearErrors } = useFormContext();
    const [isCodePostalValide, setIsCodePostalValide] = useState(false);

    const fieldName = (name) => (prefix ? `${prefix}.${name}` : name);
    const fieldId = (name) => (prefix ? `${prefix}-${name}` : name);
    const getError = (name) =>
        prefix ? formState.errors?.[prefix]?.[name] : formState.errors?.[name];
    const hasTouched = (name) =>
        prefix ? formState.touchedFields?.[prefix]?.[name] : formState.touchedFields?.[name];

    const verifierCodePostal = (codePostal) => {
        getApi(
            usagerClientReducer,
            API_VERIFIER_CODE_POSTAL_EXISTANT + codePostal,
            CONTEXT_API_VERIFIER_CODE_POSTAL,
        );
        return () => {
            reinitialiseApi(CONTEXT_API_VERIFIER_CODE_POSTAL);
        };
    };

    useEffect(() => {
        if (!prefix) {
            dispatch(reinitialiseAPI(CONTEXT_API_VERIFIER_CODE_POSTAL));
        }
    }, []);

    useEffect(() => {
        if (
            watch(fieldName('codePostal')) &&
            REGEX_RGA.exec(watch(fieldName('codePostal'))) &&
            watch(fieldName('codePostal')).length === 5
        ) {
            verifierCodePostal(watch(fieldName('codePostal')));
        }
    }, [watch(fieldName('codePostal'))]);

    useEffect(() => {
        if (watch(fieldName('codePostal')) && watch(fieldName('codePostal')).length === 5) {
            if (!REGEX_RGA.exec(watch(fieldName('codePostal'))) || errorCodePostal) {
                setError(fieldName('codePostal'), {
                    type: 'manual',
                    message:
                        messageCodePostal ??
                        'Aucune préfecture associée au code postal renseigné. Merci de vérifier votre saisie',
                });
            }
            setIsCodePostalValide(!errorCodePostal && dataCodePostal);
        } else {
            clearErrors(fieldName('codePostal'));
            setIsCodePostalValide(false);
        }
    }, [
        isLoadingCodePostal,
        errorCodePostal,
        dataCodePostal,
        messageCodePostal,
        watch(fieldName('codePostal')),
    ]);

    useEffect(() => {
        getApi(
            usagerClientReducer,
            API_RECUPERER_ADRESSE_LISTE_EXTENSION,
            CONTEXT_API_RECUPERER_ADRESSE_LISTE_EXTENSION,
        );
        return () => {
            reinitialiseApi(API_RECUPERER_ADRESSE_LISTE_EXTENSION);
        };
    }, []);

    useEffect(() => {
        if (hasTouched('typeEtNomDeVoie') || hasTouched('lieuDit')) {
            trigger([fieldName('typeEtNomDeVoie'), fieldName('lieuDit')]);
        }
    }, [
        hasTouched('typeEtNomDeVoie'),
        hasTouched('lieuDit'),
        watch(fieldName('typeEtNomDeVoie')),
        watch(fieldName('lieuDit')),
    ]);

    const extensionsAdresse = useMemo(() => {
        const extensionsAdresseVide = [{ key: '', value: '', libelle: '' }];

        if (!isLoadingListeExtension && !errorListeExtension && dataListeExtension) {
            const formaterExtension = dataListeExtension.map((extension) => {
                return { key: extension, value: extension, libelle: extension };
            });

            return extensionsAdresseVide.concat(formaterExtension);
        }

        return extensionsAdresseVide;
    }, [isLoadingListeExtension, dataListeExtension, errorListeExtension]);

    return (
        <>
            <Row gutters>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="address-line1"
                        label="Numéro de voie"
                        type="text"
                        maxLength="10"
                        disabled={desactiverChamps && autofilledFields?.numeroDeVoie}
                        {...register('numeroDeVoie')}
                        id={fieldId('numeroDeVoie')}
                        {...register(fieldName('numeroDeVoie'))}
                        uppercase
                    />
                </Col>
                <Col col={12} lg={6}>
                    <SelectControl
                        id={fieldId('extension')}
                        label="Extension"
                        disabled={desactiverChamps && autofilledFields?.extension}
                        uppercase
                        options={formaterOptions(extensionsAdresse, true)}
                        {...register(fieldName('extension'))}
                    />
                </Col>
            </Row>
            <Row gutters>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="address-line2"
                        label="Type et nom de voie *"
                        type="text"
                        maxLength="60"
                        uppercase
                        disabled={desactiverChamps && autofilledFields?.typeEtNomDeVoie}
                        id={fieldId('typeEtNomDeVoie')}
                        {...register(fieldName('typeEtNomDeVoie'), {
                            required: {
                                value: !watch(fieldName('lieuDit')),
                                message: getMessageErreurChampManquant('Type et nom de voie'),
                            },
                        })}
                        state={getError('typeEtNomDeVoie') ? 'error' : null}
                        message={getError('typeEtNomDeVoie')?.message}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        autocomplete="address-line3"
                        label="Lieu-dit"
                        type="text"
                        maxLength="60"
                        uppercase
                        disabled={desactiverChamps && autofilledFields?.lieuDit}
                        id={fieldId('lieuDit')}
                        {...register(fieldName('lieuDit'))}
                    />
                </Col>
            </Row>
            <Row gutters verticalAlign="bottom">
                <Col col={12} lg={6}>
                    <InputControl
                        label="Code postal *"
                        hintText="Exemple : 77610"
                        required
                        type="text"
                        maxLength="5"
                        minLength="5"
                        disabled={desactiverChamps && autofilledFields?.codePostal}
                        id={fieldId('codePostal')}
                        {...register(fieldName('codePostal'), {
                            required: getMessageErreurChampManquant('Code postal'),
                            pattern: {
                                value: REGEXP_CODE_POSTAL,
                                message:
                                    "Le format du code postal n'est pas valide ( Exemple: 77610 )",
                            },
                            validate: (value) =>
                                verifierSiDepartementEstOuvertALaCreationDeCompte(value),
                        })}
                        state={getError('codePostal') ? 'error' : null}
                        message={getError('codePostal')?.message}
                        valid={isCodePostalValide ? { message: 'Le code postal est valide' } : null}
                    />
                </Col>
                <Col col={12} lg={6}>
                    <InputControl
                        label="Ville *"
                        required
                        type="text"
                        maxLength="60"
                        uppercase
                        disabled={desactiverChamps && autofilledFields?.ville}
                        id={fieldId('ville')}
                        {...register(fieldName('ville'), {
                            required: getMessageErreurChampManquant('Ville'),
                        })}
                        state={getError('ville') ? 'error' : null}
                        message={getError('ville')?.message}
                    />
                </Col>
            </Row>
        </>
    );
};

FormulaireAdresseSimple.propTypes = {
    prefix: PropTypes.string,
    getApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    isLoadingListeExtension: PropTypes.bool,
    dataListeExtension: PropTypes.bool,
    errorListeExtension: PropTypes.bool,
    isLoadingCodePostal: PropTypes.bool,
    dataCodePostal: PropTypes.bool,
    errorCodePostal: PropTypes.bool,
    desactiverChamps: PropTypes.bool,
    autofilledFields: PropTypes.object,
    messageCodePostal: PropTypes.string,
};

export default connect(
    (state) => {
        const {
            isLoading: isLoadingListeExtension,
            data: dataListeExtension,
            erreur: errorListeExtension,
        } = state.adresseExtensions.recupererListe;
        const {
            isLoading: isLoadingCodePostal,
            data: dataCodePostal,
            erreur: errorCodePostal,
            message: messageCodePostal,
        } = state.monCompte.verifierCodePostal;
        return {
            isLoadingListeExtension,
            dataListeExtension,
            errorListeExtension,
            isLoadingCodePostal,
            dataCodePostal,
            errorCodePostal,
            messageCodePostal,
        };
    },
    {
        getApi: getAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(FormulaireAdresseSimple);
